import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { AuthService } from '../auth.service';
import { TranslationGeneralService } from '../../shared/translation/translation.general.service';

/**
 * The path of the endpoint: a hosting rewrite to `whocan-authCode` (firebase.json, both whocan targets) - same
 * origin, no CORS. Absolute on purpose: a relative `auth-code` would resolve against the `<base href="/de/">`
 * of the locale build. For `ng serve` the dev server proxies the path to www.whocan.dev
 * (`projects/whocan-app/proxy.conf.json`).
 */
export const AUTH_CODE_PATH = '/auth-code';

/** what the service needs of fetch - the global one, replaceable in specs */
export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** why a code is requested - the server decides from intent + bearer what `confirm` does (P1 report §3) */
export type AuthCodeIntent = 'signup' | 'upgrade' | 'login' | 'changeEmail';

/** the languages the mail exists in; the server falls back to `en` for anything else */
export type AuthCodeLanguage = 'en' | 'de' | 'fr' | 'es' | 'it' | 'pt';

/**
 * What `confirm` did with the account:
 *  - `created`      a new account (hard gate: the account exists only now)
 *  - `signedIn`     an existing account, verified by now
 *  - `upgraded`     the anonymous uid of the request kept its uid and got the address
 *  - `merge`        the token belongs to the existing account of the address; the anonymous session's items
 *                   were stashed before and are applied under that uid afterwards
 *  - `emailChanged` the signed-in account carries the new address (P3)
 */
export type AuthCodeOutcome = 'created' | 'signedIn' | 'upgraded' | 'merge' | 'emailChanged';

/**
 * Why a call failed, as the component shows it (P1 report §3):
 *  - `invalid-email`        400: the address fails the syntax check (request) or Firebase Auth rejects it (confirm)
 *  - `email-undeliverable`  400: the domain has no MX/A record - a typo, most likely
 *  - `invalid-request`      400: intent and bearer do not match (anonymous bearer on signup, confirm from another
 *                           session than request, ...) - the component redetermines the intent
 *  - `invalid-token`        401: the bearer was invalid even after one refresh
 *  - `code-invalid`         400: wrong, expired, consumed, 5 misses, IP limit - one message
 *  - `email-in-use`         409: changeEmail onto a taken address (P3)
 *  - `no-session`           the call needs a bearer, but nobody is signed in
 *  - `network`              fetch itself failed (offline, DNS, aborted)
 *  - `error`                everything else (transport errors, 500, unexpected reply)
 */
export type AuthCodeErrorKind =
    | 'invalid-email' | 'email-undeliverable' | 'invalid-request' | 'invalid-token' | 'code-invalid' | 'email-in-use'
    | 'no-session' | 'network' | 'error';

export interface AuthCodeRequestInput {
    email: string;
    intent: AuthCodeIntent;
    /** required for `signup`, optional for `upgrade` (the server keeps a name set in the app when it is missing) */
    displayName?: string;
    language: AuthCodeLanguage;
}

export interface AuthCodeConfirmInput {
    email: string;
    /** exactly 6 digits as a string - leading zeros matter */
    code: string;
}

export interface AuthCodeRequestResult {
    sent: true;
    /** the mail went into the queue instead of out at once - the component shows the delay hint (A3) */
    delayed: boolean;
}

export interface AuthCodeConfirmResult {
    outcome: AuthCodeOutcome;
    /** for `signInWithCustomToken` */
    token: string;
}

/** the error of the api service - the kind tells the component what to show */
export class AuthCodeApiError extends Error {
    constructor(public readonly kind: AuthCodeErrorKind, public readonly status: number | null, message: string) {
        super(message);
        this.name = 'AuthCodeApiError';
    }
}

/**
 * The client of the `whocan-authCode` endpoint (code auth P2, task 1; concept §4.1, P1 report §3 as the contract):
 * `request` asks for a code to an address, `confirm` trades address + code for a custom token. Both are a POST
 * with `{ action, ... }` on the same path; `withBearer` puts the Firebase ID token of the signed-in (anonymous)
 * user into the Authorization header - the server needs it for `upgrade` and `changeEmail`, and it must be the
 * same session for `request` and `confirm`.
 *
 * A 401 `invalid-token` is retried exactly once with a forced token refresh (the cached token may have expired
 * between the two steps); a second 401 is thrown. Errors become an `AuthCodeApiError` with a kind from status
 * and `body.error`. Neither the address, nor the code, nor a token is ever logged - the log lines carry the
 * action, the status and the kind.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthCodeApiService {
    /** the fetch the service calls - specs replace it */
    public fetchFn: FetchLike = (input, init) => fetch(input, init);

    constructor(
        private authService: AuthService,
        private trans: TranslationGeneralService,
        private logger: LoggerService,
    ) { }

    /** asks the server to mail a code to the address; `delayed` says the mail went through the queue */
    public async request(input: AuthCodeRequestInput, withBearer: boolean): Promise<AuthCodeRequestResult> {
        const body: Record<string, unknown> = {
            action: 'request',
            email: input.email,
            intent: input.intent,
            language: input.language,
        };
        if (input.displayName) body['displayName'] = input.displayName;
        const json = await this.post('request', body, withBearer);
        if (json?.sent !== true || typeof json.delayed !== 'boolean') {
            this.logger.warn('AuthCodeApiService: unexpected reply', 'request');
            throw new AuthCodeApiError('error', 200, 'auth code: unexpected reply');
        }
        return { sent: true, delayed: json.delayed };
    }

    /** trades address + code for the outcome and a custom token */
    public async confirm(input: AuthCodeConfirmInput, withBearer: boolean): Promise<AuthCodeConfirmResult> {
        const json = await this.post('confirm', { action: 'confirm', email: input.email, code: input.code }, withBearer);
        if (typeof json?.outcome !== 'string' || typeof json.token !== 'string' || !json.token) {
            this.logger.warn('AuthCodeApiService: unexpected reply', 'confirm');
            throw new AuthCodeApiError('error', 200, 'auth code: unexpected reply');
        }
        return { outcome: json.outcome as AuthCodeOutcome, token: json.token };
    }

    /** the language of the mail: the current locale (one of the six; the server maps anything else to `en`) */
    public get language(): AuthCodeLanguage {
        return this.trans.local as AuthCodeLanguage;
    }

    /**
     * one POST, with the retry on `invalid-token`: the second attempt carries a freshly refreshed token;
     * when that one is refused too, the error is thrown
     */
    private async post(action: 'request' | 'confirm', body: object, withBearer: boolean): Promise<any> {
        try {
            return await this.postOnce(action, body, withBearer, false);
        } catch (error) {
            if (withBearer && error instanceof AuthCodeApiError && error.kind === 'invalid-token') {
                this.logger.log('AuthCodeApiService: token refused, retrying once with a refreshed token', action);
                return await this.postOnce(action, body, withBearer, true);
            }
            throw error;
        }
    }

    private async postOnce(action: 'request' | 'confirm', body: object, withBearer: boolean, forceRefresh: boolean): Promise<any> {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (withBearer) {
            const idToken = await this.authService.getIdToken(forceRefresh);
            if (!idToken) {
                throw new AuthCodeApiError('no-session', null, 'auth code: no signed-in user');
            }
            headers['Authorization'] = 'Bearer ' + idToken;
        }
        let response: Response;
        try {
            response = await this.fetchFn(AUTH_CODE_PATH, {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
                credentials: 'omit',
            });
        } catch (error) {
            // offline, DNS, aborted - the body holds the address (and the code), so the error is not logged as is
            this.logger.warn('AuthCodeApiService: request failed', action, String((error as Error)?.message ?? error).slice(0, 120));
            throw new AuthCodeApiError('network', null, 'auth code: request failed');
        }
        const json = await this.readJson(response);
        if (!response.ok) {
            const kind = this.kindOf(response.status, json?.error);
            this.logger.log('AuthCodeApiService: refused', action, response.status, json?.error ?? null, kind);
            throw new AuthCodeApiError(kind, response.status, 'auth code: ' + (json?.error ?? response.status));
        }
        return json;
    }

    private async readJson(response: Response): Promise<any> {
        try {
            const text = await response.text();
            return text ? JSON.parse(text) : null;
        } catch {
            return null;
        }
    }

    /** the error kind of a refused call - the status decides, the error code narrows (P1 report §3) */
    private kindOf(status: number, errorCode: unknown): AuthCodeErrorKind {
        if (status === 401) return 'invalid-token';
        if (status === 409) return 'email-in-use';
        if (status === 400) {
            switch (errorCode) {
                case 'invalid-email': return 'invalid-email';
                case 'email-undeliverable': return 'email-undeliverable';
                case 'invalid-request': return 'invalid-request';
                case 'code-invalid': return 'code-invalid';
                default: return 'error'; // invalid-body and the like
            }
        }
        return 'error';
    }
}
